
import React from 'react';
import { Card } from "@/components/ui/card";
import { Clock } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { PerformanceData } from './types';
import { useIsMobile } from '@/hooks/use-mobile';

interface TimePerformanceProps {
  performanceData: PerformanceData[];
}

export const TimePerformance: React.FC<TimePerformanceProps> = ({ performanceData }) => {
  const { isMobile } = useIsMobile();
  
  const timeBasedPerformance = React.useMemo(() => 
    Array.from({ length: 24 }, (_, hour) => {
      const attempts = performanceData.filter(d => {
        const attemptHour = new Date(d.created_at).getHours();
        return attemptHour === hour;
      });
      return {
        hour: `${hour}:00`,
        averageScore: attempts.length > 0 
          ? attempts.reduce((acc, curr) => acc + curr.score, 0) / attempts.length 
          : 0,
        attempts: attempts.length
      };
    }),
    [performanceData]
  );

  // For mobile, we'll show fewer hour points to avoid crowding
  const displayData = isMobile 
    ? timeBasedPerformance.filter((_, index) => index % 3 === 0) 
    : timeBasedPerformance;

  return (
    <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-blue-50 to-white hover:shadow-lg transition-shadow duration-300`}>
      <h2 className={`${isMobile ? "text-base" : "text-xl"} font-semibold mb-4 flex items-center gap-2 text-blue-800`}>
        <Clock className="w-5 h-5 text-blue-500" />
        Performance by Time of Day
      </h2>
      <div className="w-full">
        <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
          <AreaChart 
            data={displayData}
            margin={isMobile ? { top: 5, right: 5, bottom: 20, left: 0 } : { top: 10, right: 10, bottom: 20, left: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis 
              dataKey="hour" 
              stroke="#6B7280"
              tick={{ fill: '#6B7280', fontSize: isMobile ? 10 : 12 }}
              angle={isMobile ? -45 : 0}
              textAnchor={isMobile ? "end" : "middle"}
              height={isMobile ? 40 : 30}
            />
            <YAxis 
              stroke="#6B7280"
              tick={{ fill: '#6B7280', fontSize: isMobile ? 10 : 12 }}
              width={isMobile ? 30 : 40}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: isMobile ? '10px' : '12px',
                padding: isMobile ? '4px 8px' : '8px 10px'
              }}
              formatter={(value: number) => [`${value.toFixed(1)}%`, 'Average Score']}
            />
            <Area
              type="monotone"
              dataKey="averageScore"
              stroke="#3B82F6"
              fill="url(#colorGradient)"
              strokeWidth={isMobile ? 2 : 3}
            />
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};
